import { Stack } from '@mantine/core';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from '../../../../../hooks/useDeepCompareEffect';
import { useCostTabStore } from '../../../../../store/strategy-store/strategy-store';
import {
  setCostMode,
  setCostSelectedEpics,
  setCostSelectedInitiatives,
  setCostSelectedTeams,
} from '../../../../../store/strategy-store/strategy-store.actions';
import { useCurrentStrategyTab } from '../../../strategy.hooks';
import { StrategyTab } from '../../../strategy.type';
import { MainSectionControls } from '../../components/main-section-controls/main-section-controls';
import { hasArrayData } from '../../initiative-performance.helpers';
import { useChartData, useCostEpics, useCostInitiatives, useCostTeams } from '../cost-tab.hooks';
import { CostMode } from '../cost-tab.type';
import { TreeMapChart } from './tree-map-chart/tree-map-chart';

export const MainSection = () => {
  const i18n = useTranslation();
  const currentStrategyTab = useCurrentStrategyTab();
  const mode = useCostTabStore((state) => state.mode);

  const epics = useCostEpics();
  const teams = useCostTeams();
  const initiatives = useCostInitiatives();

  useDeepCompareEffect(() => {
    if (epics.length > 0) {
      setCostSelectedEpics(epics);
    }
  }, [epics]);

  useDeepCompareEffect(() => {
    if (teams.length > 0) {
      setCostSelectedTeams(teams);
    }
  }, [teams]);

  useDeepCompareEffect(() => {
    if (initiatives.length > 0) {
      setCostSelectedInitiatives(initiatives);
    }
  }, [initiatives]);

  useEffect(() => {
    // outcomes does not support epics, so we need to set the mode to teams
    if (currentStrategyTab === StrategyTab.Outcomes || currentStrategyTab === StrategyTab.Goals) {
      if (mode === CostMode.ContributingEpics) {
        setCostMode(CostMode.ContributingTeams);
      }
    }
    // initiatives does not support initiatives, so we need to set the mode to epics
    if (currentStrategyTab === StrategyTab.Initiatives) {
      if (mode === CostMode.ContributingInitiatives) {
        setCostMode(CostMode.ContributingEpics);
      }
    }
  }, [currentStrategyTab, mode]);

  const chartRef = useRef<HTMLDivElement>(null!);
  const chartData = useChartData();

  const getTitle = (): string | null => {
    switch (mode) {
      case CostMode.ContributingEpics:
        return i18n.t('strategy.initiative_performance.labels.contributing_epics');
      case CostMode.ContributingTeams:
        return i18n.t('strategy.initiative_performance.labels.contributing_teams');
      case CostMode.ContributingInitiatives:
        return i18n.t('strategy.initiative_performance.labels.contributing_initiatives');
      default:
        return null;
    }
  };

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <MainSectionControls title={getTitle()} chartRef={chartRef} disableControls={!hasArrayData(chartData)} />
      <TreeMapChart ref={chartRef} />
    </Stack>
  );
};
